import type { MarketQuote } from '@/types';

// ---------------------------------------------------------------------------
// Stocks & Equity Service — Yahoo Finance via Vite middleware (/api/stocks)
// Fallback: synthetic seeded data matching real approximate values
// ---------------------------------------------------------------------------

// Approximate base prices (March 2026) for synthetic fallback
const EQUITY_FALLBACK: { symbol: string; name: string; base: number; subCat: string }[] = [
  // US Mega Cap
  { symbol: 'AAPL',      name: 'Apple Inc.',           base: 235,   subCat: 'us-mega' },
  { symbol: 'MSFT',      name: 'Microsoft Corp.',       base: 415,   subCat: 'us-mega' },
  { symbol: 'NVDA',      name: 'Nvidia Corp.',          base: 875,   subCat: 'us-mega' },
  { symbol: 'GOOGL',     name: 'Alphabet Inc.',         base: 185,   subCat: 'us-mega' },
  { symbol: 'AMZN',      name: 'Amazon.com',            base: 205,   subCat: 'us-mega' },
  { symbol: 'TSLA',      name: 'Tesla Inc.',            base: 270,   subCat: 'us-mega' },
  { symbol: 'META',      name: 'Meta Platforms',        base: 620,   subCat: 'us-mega' },
  { symbol: 'AVGO',      name: 'Broadcom Inc.',         base: 225,   subCat: 'us-mega' },
  { symbol: 'AMD',       name: 'Advanced Micro Devices',base: 135,   subCat: 'us-mega' },
  { symbol: 'ORCL',      name: 'Oracle Corp.',          base: 165,   subCat: 'us-mega' },
  { symbol: 'CRM',       name: 'Salesforce Inc.',       base: 315,   subCat: 'us-mega' },
  { symbol: 'ADBE',      name: 'Adobe Inc.',            base: 460,   subCat: 'us-mega' },
  { symbol: 'COST',      name: 'Costco Wholesale',      base: 965,   subCat: 'us-mega' },
  { symbol: 'UNH',       name: 'UnitedHealth Group',    base: 490,   subCat: 'us-mega' },
  { symbol: 'V',         name: 'Visa Inc.',             base: 330,   subCat: 'finance' },
  { symbol: 'MA',        name: 'Mastercard Inc.',       base: 510,   subCat: 'finance' },
  { symbol: 'XOM',       name: 'Exxon Mobil Corp.',     base: 112,   subCat: 'us-mega' },
  { symbol: 'JPM',       name: 'JPMorgan Chase',        base: 250,   subCat: 'finance' },
  { symbol: 'BAC',       name: 'Bank of America',       base: 45,    subCat: 'finance' },
  { symbol: 'GS',        name: 'Goldman Sachs',         base: 540,   subCat: 'finance' },
  // US Defense
  { symbol: 'LMT',       name: 'Lockheed Martin',       base: 490,   subCat: 'defense' },
  { symbol: 'RTX',       name: 'RTX Corporation',       base: 130,   subCat: 'defense' },
  { symbol: 'NOC',       name: 'Northrop Grumman',      base: 520,   subCat: 'defense' },
  { symbol: 'BA',        name: 'Boeing Company',        base: 180,   subCat: 'defense' },
  { symbol: 'GD',        name: 'General Dynamics',      base: 285,   subCat: 'defense' },
  // US Indices
  { symbol: '^GSPC',     name: 'S&P 500',               base: 5850,  subCat: 'us-index' },
  { symbol: '^IXIC',     name: 'NASDAQ Composite',      base: 19200, subCat: 'us-index' },
  { symbol: '^DJI',      name: 'Dow Jones Ind. Avg',    base: 43200, subCat: 'us-index' },
  { symbol: '^RUT',      name: 'Russell 2000',          base: 2180,  subCat: 'us-index' },
  { symbol: '^VIX',      name: 'CBOE VIX Fear Index',   base: 18.5,  subCat: 'us-index' },
  // Asia Indices
  { symbol: '^N225',     name: 'Nikkei 225',            base: 38200, subCat: 'asia' },
  { symbol: '^HSI',      name: 'Hang Seng Index',       base: 21500, subCat: 'asia' },
  { symbol: '000001.SS', name: 'Shanghai Composite',    base: 3350,  subCat: 'asia' },
  { symbol: '^KS11',     name: 'KOSPI',                 base: 2450,  subCat: 'asia' },
  { symbol: '^BSESN',    name: 'BSE Sensex',            base: 74500, subCat: 'asia' },
  { symbol: '^AXJO',     name: 'ASX 200',               base: 8200,  subCat: 'asia' },
  { symbol: '^TWII',     name: 'TAIEX (Taiwan)',         base: 22800, subCat: 'asia' },
  // Europe / LatAm Indices
  { symbol: '^FTSE',     name: 'FTSE 100',              base: 8450,  subCat: 'europe' },
  { symbol: '^GDAXI',    name: 'DAX (Germany)',          base: 22500, subCat: 'europe' },
  { symbol: '^FCHI',     name: 'CAC 40 (France)',        base: 8200,  subCat: 'europe' },
  { symbol: '^BVSP',     name: 'Bovespa (Brazil)',       base: 129000,subCat: 'europe' }, // latam
];

const BOND_ETF_FALLBACK: { symbol: string; name: string; base: number; desc: string }[] = [
  { symbol: 'TLT',  name: 'iShares 20+ Yr Treasury', base: 91,   desc: 'Long-term US Gov Bond' },
  { symbol: 'IEF',  name: 'iShares 7-10 Yr Treasury',base: 100,  desc: '7-10Y US Treasury' },
  { symbol: 'SHY',  name: 'iShares 1-3 Yr Treasury',  base: 83,   desc: 'Short-term Treasury' },
  { symbol: 'HYG',  name: 'iShares High Yield Bond',  base: 78,   desc: 'High Yield Corporate' },
  { symbol: 'EMB',  name: 'iShares EM Bond USD',      base: 89,   desc: 'Emerging Market Bond' },
  { symbol: 'LQD',  name: 'iShares IG Corp Bond',     base: 107,  desc: 'Investment Grade Corp' },
];

// Synthetic yield curve (realistic 2026 estimates)
const YIELD_CURVE_FALLBACK: { symbol: string; name: string; base: number; subCat: string }[] = [
  { symbol: 'US02Y', name: 'US 2Y Treasury Yield',    base: 4.25, subCat: 'yield' },
  { symbol: 'US10Y', name: 'US 10Y Treasury Yield',   base: 4.55, subCat: 'yield' },
  { symbol: 'US30Y', name: 'US 30Y Treasury Yield',   base: 4.82, subCat: 'yield' },
  { symbol: 'DE10Y', name: 'Germany Bund 10Y Yield',  base: 2.65, subCat: 'yield' },
  { symbol: 'UK10Y', name: 'UK Gilt 10Y Yield',       base: 4.45, subCat: 'yield' },
  { symbol: 'JP10Y', name: 'Japan JGB 10Y Yield',     base: 1.45, subCat: 'yield' },
  { symbol: 'CN10Y', name: 'China 10Y Bond Yield',    base: 2.10, subCat: 'yield' },
  { symbol: 'KR10Y', name: 'Korea 10Y Bond Yield',    base: 3.20, subCat: 'yield' },
  { symbol: 'IN10Y', name: 'India 10Y Bond Yield',    base: 6.85, subCat: 'yield' },
  { symbol: 'BR10Y', name: 'Brazil 10Y Bond Yield',   base: 14.8, subCat: 'yield' },
  { symbol: 'TR10Y', name: 'Turkey 10Y Bond Yield',   base: 42.5, subCat: 'yield' },
];

function dailySeed(offset: number): number {
  const d = new Date();
  return Math.sin((d.getFullYear() * 365 + d.getMonth() * 31 + d.getDate() + offset) * 2.71828);
}

export function fetchSyntheticEquity(): MarketQuote[] {
  const now = new Date().toISOString();
  return EQUITY_FALLBACK.map((e, i): MarketQuote => {
    const s = dailySeed(i + 100);
    const changePct = parseFloat((s * 2.5).toFixed(2));
    const price = parseFloat((e.base * (1 + s * 0.015)).toFixed(e.base > 100 ? 2 : 3));
    return {
      symbol: e.symbol,
      name: e.name,
      price,
      change: parseFloat((price * changePct / 100).toFixed(2)),
      changePercent: changePct,
      timestamp: now,
      category: 'equity',
      subCategory: e.subCat,
      unit: e.symbol.startsWith('^') ? 'index' : 'USD',
    };
  });
}

export function fetchSyntheticBonds(): MarketQuote[] {
  const now = new Date().toISOString();
  const etfs = BOND_ETF_FALLBACK.map((b, i): MarketQuote => {
    const s = dailySeed(i + 200);
    const price = parseFloat((b.base + s * 1.5).toFixed(2));
    return {
      symbol: b.symbol,
      name: b.name,
      price,
      change: parseFloat((s * 0.5).toFixed(2)),
      changePercent: parseFloat((s * 0.6).toFixed(2)),
      timestamp: now,
      category: 'bonds',
      subCategory: 'bond-etf',
      unit: 'USD',
    };
  });

  const yields = YIELD_CURVE_FALLBACK.map((y, i): MarketQuote => {
    const s = dailySeed(i + 210);
    const price = parseFloat((y.base + s * 0.08).toFixed(3));
    return {
      symbol: y.symbol,
      name: y.name,
      price,
      change: parseFloat((s * 0.04).toFixed(3)),
      changePercent: parseFloat((s * 0.9).toFixed(2)),
      timestamp: now,
      category: 'bonds',
      subCategory: y.subCat,
      unit: '%',
    };
  });

  return [...yields, ...etfs];
}

// 실시간 Yahoo 선물/지표 → 기존 synthetic 심볼·카테고리로 매핑.
// 심볼이 일치하면 useDataFetch 병합 단계에서 실데이터가 synthetic을 대체한다.
const REALTIME_FUTURES_MAP: Record<string, {
  symbol: string; name: string; category: MarketQuote['category'];
  subCategory?: string; unit: string; divisor?: number;
}> = {
  'CL=F':     { symbol: 'WTI',    name: 'Crude Oil WTI',         category: 'energy',    unit: '$/bbl' },
  'BZ=F':     { symbol: 'BRENT',  name: 'Brent Crude',           category: 'energy',    unit: '$/bbl' },
  'NG=F':     { symbol: 'NG',     name: 'Natural Gas',           category: 'energy',    unit: '$/MMBtu' },
  'GC=F':     { symbol: 'GOLD',   name: 'Gold (COMEX)',          category: 'commodity', unit: '$/oz' },
  'SI=F':     { symbol: 'SILVER', name: 'Silver',                category: 'commodity', unit: '$/oz' },
  'HG=F':     { symbol: 'COPPER', name: 'Copper',                category: 'commodity', unit: '$/lb' },
  'ZW=F':     { symbol: 'WHEAT',  name: 'Wheat (CBOT)',          category: 'food',      unit: '$/bu', divisor: 100 }, // 센트/bu → $/bu
  'ZC=F':     { symbol: 'CORN',   name: 'Corn (CBOT)',           category: 'food',      unit: '$/bu', divisor: 100 },
  '^TNX':     { symbol: 'US10Y',  name: 'US 10Y Treasury Yield', category: 'bonds',     subCategory: 'yield', unit: '%' },
  'DX-Y.NYB': { symbol: 'DXY',    name: 'US Dollar Index',       category: 'macro',     unit: 'index' },
};

function mapRealtimeQuote(q: MarketQuote): MarketQuote {
  const m = REALTIME_FUTURES_MAP[q.symbol];
  if (!m) return q;
  const d = m.divisor ?? 1;
  return {
    ...q,
    symbol: m.symbol,
    name: m.name,
    category: m.category,
    subCategory: m.subCategory ?? q.subCategory,
    unit: m.unit,
    price: +(q.price / d).toFixed(3),
    change: +(q.change / d).toFixed(3),
  };
}

export async function fetchStockQuotes(): Promise<MarketQuote[]> {
  try {
    const res = await fetch('/api/stocks', { signal: AbortSignal.timeout(15_000) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json() as MarketQuote[];
    if (!Array.isArray(data) || data.length === 0) throw new Error('empty');
    return data.map(mapRealtimeQuote);
  } catch {
    return fetchSyntheticEquity();
  }
}
